import React, { useState, useRef } from 'react';
import { Form, Input, Button, Select, Alert, Icon, Row, Col } from 'antd';
import { CompanyFormData, BANKS } from './types';

const { Option } = Select;

interface EmailRowProps {
  email: string;
  showDelete: boolean;
  onChange: (v: string) => void;
  onRemove: () => void;
}

const EmailRow: React.FC<EmailRowProps> = ({ email, showDelete, onChange, onRemove }) => {
  const invalid = email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  return (
    <div style={{ display: 'grid', gridTemplateColumns: showDelete ? '1fr 36px' : '1fr', gap: 8, alignItems: 'center' }}>
      <Input
        prefix={<Icon type="mail" style={{ color: 'rgba(0,0,0,.25)' }} />}
        type="email"
        placeholder="financeiro@empresa.com.br"
        value={email}
        onChange={(e) => onChange(e.target.value)}
        style={invalid ? { borderColor: '#f5222d' } : {}}
      />
      {showDelete && (
        <Button
          type="link"
          icon="delete"
          onClick={onRemove}
          style={{ color: '#6d7583', padding: 0, width: 36, height: 36, border: '1px solid #e9ecf0', borderRadius: 8 }}
        />
      )}
    </div>
  );
};

interface Props {
  value: CompanyFormData;
  onChange: (v: CompanyFormData) => void;
  onBack: () => void;
  onNext: () => void;
  submitting?: boolean;
}

const StepFinancial: React.FC<Props> = ({ value, onChange, onBack, onNext, submitting }) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const fin = value.financial;
  const setFin = (patch: Partial<typeof fin>) =>
    onChange({ ...value, financial: { ...fin, ...patch } });

  const emails = fin.emails.length ? fin.emails : [''];

  const emailIdsRef = useRef<string[]>([]);
  while (emailIdsRef.current.length < emails.length) {
    emailIdsRef.current.push(Math.random().toString(36).slice(2));
  }
  if (emailIdsRef.current.length > emails.length) {
    emailIdsRef.current = emailIdsRef.current.slice(0, emails.length);
  }

  const setEmail = (i: number, v: string) => {
    const next = [...emails];
    next[i] = v;
    setFin({ emails: next });
  };
  const addEmail = () => setFin({ emails: [...emails, ''] });
  const removeEmail = (i: number) =>
    setFin({ emails: emails.filter((_, idx) => idx !== i) });

  const bankValid = fin.bank.trim().length > 0;
  const agencyValid = /^\d{1,6}$/.test(fin.agency.replace(/\D/g, ''));
  const accountValid = /^\d{4,12}(-\d)?$/.test(fin.account);
  const pixValid = fin.pixKey.trim().length > 0;
  const emailsValid =
    emails.filter((e) => e.trim()).length > 0 &&
    emails.every((e) => !e.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim()));

  const canNext = bankValid && agencyValid && accountValid && pixValid && emailsValid;

  const handleSubmit = () => {
    setTouched({ bank: true, agency: true, account: true, pix: true, emails: true });
    if (canNext) onNext();
  };

  return (
    <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 600, color: '#202020', marginBottom: 6 }}>
          Dados financeiros
        </h2>
        <p style={{ fontSize: 14, color: '#4c5462', lineHeight: 1.5, margin: 0 }}>
          Para operadores de rede, precisamos dos dados bancários para o repasse das sessões de carga.
        </p>
      </div>

      <Alert
        type="info"
        showIcon
        message="As informações informadas são criptografadas e acessadas apenas pela equipe financeira da Tupi."
        style={{ borderRadius: 8 }}
      />

      <Row gutter={20}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Banco"
            required
            validateStatus={touched.bank && !bankValid ? 'error' : ''}
            help={touched.bank && !bankValid ? 'Selecione o banco.' : ''}
          >
            <Select
              placeholder="Selecione o banco"
              value={fin.bank || undefined}
              onChange={(v: string) => setFin({ bank: v })}
              onBlur={() => setTouched((t) => ({ ...t, bank: true }))}
              style={{ width: '100%' }}
            >
              {BANKS.map((b) => (
                <Option key={b} value={b}>{b}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Agência"
            required
            extra="Sem o dígito, apenas números."
            validateStatus={touched.agency && !agencyValid ? 'error' : ''}
            help={touched.agency && !agencyValid ? 'Insira uma agência válida.' : ''}
          >
            <Input
              placeholder="0001"
              inputMode="numeric"
              value={fin.agency}
              onChange={(e) => setFin({ agency: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              onBlur={() => setTouched((t) => ({ ...t, agency: true }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Conta corrente"
            required
            extra="Com dígito verificador."
            validateStatus={touched.account && !accountValid ? 'error' : ''}
            help={touched.account && !accountValid ? 'Insira uma conta válida.' : ''}
          >
            <Input
              prefix={<Icon type="credit-card" style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="12345-6"
              inputMode="numeric"
              value={fin.account}
              onChange={(e) => setFin({ account: e.target.value.replace(/[^\d-]/g, '') })}
              onBlur={() => setTouched((t) => ({ ...t, account: true }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Chave Pix"
            required
            extra="CNPJ, e-mail, telefone ou chave aleatória."
            validateStatus={touched.pix && !pixValid ? 'error' : ''}
            help={touched.pix && !pixValid ? 'Informe a chave Pix.' : ''}
          >
            <Input
              prefix={<Icon type="key" style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="exemplo@empresa.com.br"
              value={fin.pixKey}
              onChange={(e) => setFin({ pixKey: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, pix: true }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* E-mails para NF */}
      <div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#202020' }}>
            <span style={{ color: '#5b00ef', marginRight: 4 }}>*</span>E-mails para cadastro e notas fiscais
          </span>
          <div style={{ fontSize: 12, color: '#6d7583' }}>
            Enviaremos para estes e-mails as mensalidades e notas fiscais. Você pode adicionar mais de um.
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {emails.map((em, i) => (
            <EmailRow
              key={emailIdsRef.current[i]}
              email={em}
              showDelete={emails.length > 1}
              onChange={(v) => setEmail(i, v)}
              onRemove={() => removeEmail(i)}
            />
          ))}
          <Button
            type="link"
            icon="plus"
            onClick={addEmail}
            style={{ alignSelf: 'flex-start', padding: 0, color: '#5b00ef' }}
          >
            Adicionar outro e-mail
          </Button>
        </div>
      </div>

      {/* Ações */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: 24,
        borderTop: '1px solid #e9ecf0',
        marginTop: 8,
      }}>
        <Button icon="arrow-left" onClick={onBack}>Voltar</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!canNext || submitting}
          loading={submitting}
          style={canNext ? { background: '#5b00ef', borderColor: '#5b00ef' } : {}}
        >
          Finalizar cadastro {!submitting && <Icon type="arrow-right" />}
        </Button>
      </div>
    </div>
  );
};

export default StepFinancial;
